import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getConsent, setConsent, type ConsentChoice } from "@/lib/consent";

// First-visit consent banner. Shown until the user makes a choice.
// "Accept" enables analytics + error telemetry; "Decline" keeps the app
// in essential-only mode. The choice persists in localStorage.
//
// Note: analytics/Sentry read consent live (track wrapper) or at next
// load (Sentry init), so declining is honored immediately for events
// and on the next session for error tracking.
const ConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (getConsent() === null) setVisible(true);
  }, []);

  const choose = (choice: ConsentChoice) => {
    setConsent(choice);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Privacy consent"
      className="fixed bottom-0 inset-x-0 z-[60] bg-card border-t border-ink shadow-[0_-18px_44px_-24px_rgba(var(--fn-shadow-rgb),0.35)]"
    >
      <div className="mx-auto max-w-5xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <p className="font-serif text-sm text-ink-muted leading-relaxed flex-1">
          We use privacy-friendly analytics and error diagnostics to improve
          SundayAtlas. See our{" "}
          <Link
            to="/privacy"
            className="underline underline-offset-2 text-accent-text hover:text-ink"
          >
            Privacy Policy
          </Link>
          .
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => choose("declined")}
            className="kicker inline-flex items-center justify-center min-h-[44px] px-5 border border-rule text-ink-muted hover:text-ink hover:bg-paper-hover transition-colors"
          >
            Decline
          </button>
          <button
            onClick={() => choose("accepted")}
            className="kicker inline-flex items-center justify-center min-h-[44px] px-6 bg-ink text-paper hover:bg-ink-soft transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
